import { useFormatPropertyValue } from "src/hooks/property/useFormatProperty";
import { useFormatTypedValue } from "src/hooks/useFormatTypedValue";
import { parseTypedValue } from "src/mappers/typedValue.mappers";
import { BlacklistedCaseIdentifier } from "src/types/case";
import { Element } from "src/types/element";
import { Identifier } from "src/types/identifier";
import { PropertyValue } from "src/types/property";
import { isEmpty } from "src/utils/arrays.utils";

import {
  ComparisonMapping,
  WidgetDefinition,
  WidgetProperty,
  WidgetPropertyDefinition,
} from "../Widget.typings";

import { useCheckMismatch } from "./mismatch";
import { DEFAULT_SEPARATOR, DEFAULT_VALUE } from "./widgetProperties.constants";

type MapSingleValuePropertyToWidgetPropertyArgs = {
  displayedPropertyDefinition: WidgetPropertyDefinition;
  propertiesOrIdentifiersData: Identifier[] | PropertyValue[];
  widgetDefinition: WidgetDefinition | undefined | null;
  blacklistedIdentifiers?: BlacklistedCaseIdentifier[];
  comparisonMapping?: ComparisonMapping | null;
  elements: Element[];
};

export const useMapSingleValuePropertyToWidgetProperty = () => {
  const formatTypedValue = useFormatTypedValue();
  const formatPropertyValue = useFormatPropertyValue();
  const checkMismatch = useCheckMismatch();

  return ({
    displayedPropertyDefinition,
    propertiesOrIdentifiersData,
    widgetDefinition,
    blacklistedIdentifiers,
    comparisonMapping,
    elements,
  }: MapSingleValuePropertyToWidgetPropertyArgs): WidgetProperty | null => {
    const propertiesType = widgetDefinition?.configuration?.propertiesType;
    const propertyCodeToFind = displayedPropertyDefinition.property;
    const foundPropertyOrIdentifier = propertiesOrIdentifiersData.find(
      (property) =>
        ("property" in property && property.property.code === propertyCodeToFind) ||
        ("schemaIdentifier" in property && property.schemaIdentifier.code === propertyCodeToFind)
    );

    const comparison = checkMismatch(
      comparisonMapping,
      elements,
      undefined,
      propertyCodeToFind,
      foundPropertyOrIdentifier
    );

    if (!foundPropertyOrIdentifier) {
      return {
        label: displayedPropertyDefinition.label ?? displayedPropertyDefinition.property,
        value: DEFAULT_VALUE,
        type: "Identifier",
        code: displayedPropertyDefinition.code,
        elementCode: displayedPropertyDefinition.elementCode,
      };
    }

    const typedValue =
      "property" in foundPropertyOrIdentifier
        ? foundPropertyOrIdentifier.value
        : parseTypedValue(foundPropertyOrIdentifier.value, "string");
    const formattedValue =
      "property" in foundPropertyOrIdentifier
        ? formatPropertyValue(foundPropertyOrIdentifier)
        : formatTypedValue(typedValue);

    const name =
      "property" in foundPropertyOrIdentifier
        ? foundPropertyOrIdentifier.property.name
        : foundPropertyOrIdentifier.schemaIdentifier.name;
    const id = "property" in foundPropertyOrIdentifier ? undefined : foundPropertyOrIdentifier.id;

    const code =
      "property" in foundPropertyOrIdentifier
        ? foundPropertyOrIdentifier.property.code
        : foundPropertyOrIdentifier.schemaIdentifier.code;

    // Normalize values for comparison
    // typedValue is a TypedValue object for identifiers, or could be TypedValue/string for properties
    const normalizedValue =
      (typeof typedValue === "string" ? typedValue : typedValue?.value)?.toString().trim() ?? "";

    const elementCode = displayedPropertyDefinition.elementCode;

    const blacklist = blacklistedIdentifiers?.find((blacklistedIdentifier) => {
      const identifierTypeCode = blacklistedIdentifier.identifier.identifierType.code;
      const blacklistedSchemaCode = blacklistedIdentifier.identifier.schemaIdentifier?.code;
      const blacklistedValue = String(blacklistedIdentifier.identifier.value ?? "").trim();
      const elementMatches = !elementCode || blacklistedIdentifier.element.typeCode === elementCode;

      const typeMatches = identifierTypeCode === code || blacklistedSchemaCode === code;

      return typeMatches && blacklistedValue === normalizedValue && elementMatches;
    })?.blacklistElement.blacklist.name;

    return {
      label: displayedPropertyDefinition.label ?? name,
      value: formattedValue,
      blacklist: blacklist,
      type: displayedPropertyDefinition.type ?? propertiesType,
      code: code,
      elementCode: displayedPropertyDefinition.elementCode,
      mismatch: comparison?.mismatch,
      id: id,
    };
  };
};

export const useMapMultiValuePropertyToWidgetProperty = () => {
  const formatTypedValue = useFormatTypedValue();
  const formatPropertyValue = useFormatPropertyValue();
  const checkMismatch = useCheckMismatch();

  return ({
    displayedPropertyDefinition,
    propertiesOrIdentifiersData,
    widgetDefinition,
    blacklistedIdentifiers,
    comparisonMapping,
    elements,
  }: MapSingleValuePropertyToWidgetPropertyArgs): WidgetProperty | null => {
    const propertiesType = widgetDefinition?.configuration?.propertiesType;
    if (!displayedPropertyDefinition.properties) return null;

    const foundProperties = displayedPropertyDefinition.properties.reduce<
      Identifier[] | PropertyValue[]
    >((acc, propertyCode) => {
      const foundProperty = propertiesOrIdentifiersData.find(
        (property) =>
          ("property" in property && property.property.code === propertyCode) ||
          ("schemaIdentifier" in property && property.schemaIdentifier.code === propertyCode)
      );

      return (foundProperty ? [...acc, foundProperty] : acc) as Identifier[] | PropertyValue[];
    }, []);

    if (isEmpty<unknown>(foundProperties) && !displayedPropertyDefinition.label?.trim()) {
      return null;
    }

    if (isEmpty<unknown>(foundProperties)) {
      return {
        label: displayedPropertyDefinition.label,
        value: DEFAULT_VALUE,
        type: "Identifier",
        code: displayedPropertyDefinition.code,
        elementCode: displayedPropertyDefinition.elementCode,
      };
    }

    const propertyValue =
      foundProperties.length === 1
        ? "property" in foundProperties[0]
          ? formatPropertyValue(foundProperties[0])
          : foundProperties[0].value
        : foundProperties
            .map((property) => {
              if (!("property" in property)) {
                return property.value;
              }
              return formatPropertyValue(property);
            })
            .join(displayedPropertyDefinition.separator ?? DEFAULT_SEPARATOR);

    const identifierId = "property" in foundProperties[0] ? undefined : foundProperties[0].id;

    const comparison = checkMismatch(comparisonMapping, elements, foundProperties);

    const name =
      "property" in foundProperties[0]
        ? foundProperties[0].property.name
        : foundProperties[0].schemaIdentifier.name;

    const blacklistedIdentifier = blacklistedIdentifiers?.find(
      (blacklistedIdentifier) =>
        blacklistedIdentifier.identifier.identifierType.code.toUpperCase() ===
        displayedPropertyDefinition.label?.toUpperCase()
    );

    return {
      label: displayedPropertyDefinition.label ?? name,
      value:
        typeof propertyValue === "string"
          ? propertyValue
          : formatTypedValue(propertyValue) ?? DEFAULT_VALUE,
      blacklist:
        blacklistedIdentifier?.identifier.value === propertyValue
          ? blacklistedIdentifier.blacklistElement.blacklist.name
          : undefined,
      code: displayedPropertyDefinition.code,
      type: displayedPropertyDefinition.type ?? propertiesType,
      elementCode: displayedPropertyDefinition.elementCode,
      id: identifierId,
      mismatch: comparison?.mismatch,
    };
  };
};
